import React from "react";
import { motion } from "framer-motion";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import communityTable from "../assets/lottie-files/communityTable.lottie";
import { Row, Col } from "react-bootstrap";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" as const } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
};

const LightCard = ({
  children,
  style,
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <div
    style={{
      background: "#ffffff",
      border: "1px solid rgba(45,106,20,0.12)",
      borderRadius: 16,
      padding: "1.75rem",
      height: "100%",
      boxShadow: "0 2px 20px rgba(0,0,0,0.055)",
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionHeading = ({
  children,
  centered,
}: {
  children: React.ReactNode;
  centered?: boolean;
}) => (
  <div style={{ marginBottom: "2rem", textAlign: centered ? "center" : "left" }}>
    <h2
      style={{
        fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
        fontWeight: 800,
        color: "#0d1b2a",
        margin: 0,
      }}
    >
      {children}
    </h2>
    <div
      style={{
        width: 56,
        height: 4,
        background: "linear-gradient(90deg, #6dc744, #4a8c25)",
        borderRadius: 2,
        marginTop: 8,
        marginLeft: centered ? "auto" : 0,
        marginRight: centered ? "auto" : 0,
      }}
    />
  </div>
);

const CommunityTable: React.FC = () => {
  return (
    <div>
      {/* ── HERO (dark, for contrast) ── */}
      <section
        style={{
          background: "linear-gradient(135deg, #0a2605 0%, #0d1b2a 55%, #150308 100%)",
          padding: "5rem 1.5rem 6rem",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -120, right: -120, width: 440, height: 440, borderRadius: "50%", background: "radial-gradient(circle, rgba(198,37,45,0.2) 0%, transparent 70%)", pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -100, left: -100, width: 380, height: 380, borderRadius: "50%", background: "radial-gradient(circle, rgba(109,199,68,0.15) 0%, transparent 70%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "3rem", position: "relative", zIndex: 1 }}>
          <motion.div style={{ flex: "1 1 480px", minWidth: 300 }} variants={stagger} initial="hidden" animate="visible">
            <motion.span
              variants={fadeUp}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(109,199,68,0.12)", border: "1px solid rgba(109,199,68,0.3)", borderRadius: 999, padding: "0.35rem 1rem", color: "#6dc744", fontSize: "0.82rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "1rem" }}
            >
              <span style={{ width: 7, height: 7, background: "#6dc744", borderRadius: "50%", display: "inline-block" }} />
              Free Community Space
            </motion.span>

            <motion.h1
              variants={fadeUp}
              style={{ fontSize: "clamp(2.4rem, 5.5vw, 4rem)", fontWeight: 900, color: "white", lineHeight: 1.08, marginBottom: "1.25rem" }}
            >
              The{" "}
              <span style={{ background: "linear-gradient(90deg, #6dc744, #c6252d)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Community Table
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              style={{ fontSize: "1.15rem", color: "rgba(255,255,255,0.72)", lineHeight: 1.7, marginBottom: "2rem", maxWidth: 520 }}
            >
              A high-visibility space at our Saturday Markets for local groups,
              non-profits, school teams, and community-minded organizations.
              Completely <b style={{ color: "#6dc744" }}>free</b> — share your
              mission, recruit volunteers, fundraise, or simply connect.
            </motion.p>

            <motion.div variants={fadeUp}>
              <a
                href="https://forms.gle/VCoPqqmSDRJ3UgzM9"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <button
                  style={{ background: "linear-gradient(135deg, #c6252d, #a01e25)", color: "white", border: "none", borderRadius: 999, padding: "0.9rem 2.2rem", fontWeight: 700, fontSize: "1rem", cursor: "pointer", boxShadow: "0 4px 20px rgba(198,37,45,0.35)", transition: "transform 0.2s, box-shadow 0.2s" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-2px)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 8px 28px rgba(198,37,45,0.5)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 4px 20px rgba(198,37,45,0.35)"; }}
                >
                  Reserve Your Table →
                </button>
              </a>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.88 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.85, ease: "easeOut" }}
            style={{ flex: "1 1 360px", minWidth: 280, display: "flex", justifyContent: "center" }}
          >
            <DotLottieReact src={communityTable} loop autoplay style={{ width: "100%", maxWidth: 440 }} />
          </motion.div>
        </div>
      </section>

      {/* ── WHY THE COMMUNITY TABLE ── */}
      <section style={{ background: "#fffdf8", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <LightCard
              style={{
                textAlign: "center",
                borderTop: "4px solid #6dc744",
                borderColor: "rgba(45,106,20,0.15)",
                padding: "2.5rem 2rem",
              }}
            >
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🌟</div>
              <h2 style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", fontWeight: 800, color: "#0d1b2a", marginBottom: "1rem" }}>
                Why Offer the Community Table?
              </h2>
              <p style={{ fontSize: "1.12rem", color: "#4a5a40", lineHeight: 1.75, margin: 0, maxWidth: 640, marginLeft: "auto", marginRight: "auto" }}>
                We believe in supporting the amazing groups doing important work
                in our community. The Community Table offers free space in a busy
                indoor area of the Market, giving organizations the visibility
                they need to grow, share ideas, and build connections.
              </p>
            </LightCard>
          </motion.div>
        </div>
      </section>

      {/* ── WHO CAN APPLY ── */}
      <section style={{ background: "#f2fbee", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            <motion.div variants={fadeUp}>
              <SectionHeading centered>📌 Who Can Apply?</SectionHeading>
            </motion.div>

            <Row xs={1} sm={2} md={4} className="g-4">
              {[
                { icon: "🏢", color: "#4a8c25", bg: "#f2fbee", title: "Non-profits & Charities", desc: "Registered non-profit organizations and local charities." },
                { icon: "🎓", color: "#c6252d", bg: "#fdecea", title: "School Teams & Clubs", desc: "School-based teams, clubs, or student organizations." },
                { icon: "🌱", color: "#4a8c25", bg: "#f2fbee", title: "Community Groups", desc: "Grassroots initiatives and local community groups." },
                { icon: "🤲", color: "#C19A3B", bg: "#fdf6e3", title: "Community-Focused Orgs", desc: "Any organization with a community-focused mission." },
              ].map((item, i) => (
                <Col key={i}>
                  <motion.div variants={fadeUp} style={{ height: "100%" }}>
                    <LightCard style={{ textAlign: "center", background: item.bg, borderTop: `4px solid ${item.color}`, borderColor: `${item.color}22` }}>
                      <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>{item.icon}</div>
                      <h4 style={{ color: "#0d1b2a", fontWeight: 700, fontSize: "1.05rem", marginBottom: "0.5rem" }}>{item.title}</h4>
                      <p style={{ color: "#5a6a50", margin: 0, fontSize: "0.95rem", lineHeight: 1.6 }}>{item.desc}</p>
                    </LightCard>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={{ background: "#fffdf8", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}>
            <motion.div variants={fadeUp}>
              <SectionHeading centered>📝 How It Works</SectionHeading>
            </motion.div>

            <Row xs={1} md={3} className="g-4">
              {[
                { n: 1, icon: "📅", color: "#4a8c25", bg: "#f2fbee", title: "Reserve Your Date", desc: "Choose a Saturday Market date that works for your group using our online request form." },
                { n: 2, icon: "🪑", color: "#C19A3B", bg: "#fdf6e3", title: "Set Up at the Market", desc: "We'll provide a high-traffic indoor table free of charge. Bring your own signage, brochures, and friendly faces!" },
                { n: 3, icon: "🤝", color: "#c6252d", bg: "#fdecea", title: "Connect with the Community", desc: "Share your story, promote your cause, recruit volunteers, or fundraise — it's your space to use creatively." },
              ].map((step) => (
                <Col key={step.n}>
                  <motion.div variants={fadeUp} style={{ height: "100%" }}>
                    <LightCard style={{ textAlign: "center", background: step.bg, borderTop: `4px solid ${step.color}`, borderColor: `${step.color}22`, paddingTop: "2.5rem", position: "relative" }}>
                      <div style={{ position: "absolute", top: 14, left: "50%", transform: "translateX(-50%)", width: 30, height: 30, background: step.color, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.82rem", color: "white" }}>
                        {step.n}
                      </div>
                      <div style={{ fontSize: "2.8rem", marginBottom: "0.75rem" }}>{step.icon}</div>
                      <h4 style={{ color: "#0d1b2a", fontWeight: 700, fontSize: "1.05rem", marginBottom: "0.5rem" }}>{step.title}</h4>
                      <p style={{ color: "#5a6a50", margin: 0, fontSize: "0.92rem", lineHeight: 1.65 }}>{step.desc}</p>
                    </LightCard>
                  </motion.div>
                </Col>
              ))}
            </Row>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ background: "#f2fbee", padding: "4rem 1.5rem" }}>
        <div style={{ maxWidth: 680, margin: "0 auto" }}>
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <LightCard
              style={{
                textAlign: "center",
                padding: "3rem 2rem",
                borderTop: "4px solid #c6252d",
                borderColor: "rgba(198,37,45,0.15)",
                background: "white",
              }}
            >
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🤝</div>
              <h3 style={{ fontWeight: 800, fontSize: "1.6rem", color: "#0d1b2a", marginBottom: "0.75rem" }}>
                Ready to reserve your Community Table?
              </h3>
              <p style={{ fontSize: "1.1rem", color: "#5a6a50", marginBottom: "2rem", lineHeight: 1.65 }}>
                <b>Interested?</b> Learn more and request your dates below — it's completely free for qualifying organizations.
              </p>
              <a
                href="https://forms.gle/VCoPqqmSDRJ3UgzM9"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
              >
                <button
                  style={{ background: "linear-gradient(135deg, #c6252d, #a01e25)", color: "white", border: "none", borderRadius: 999, padding: "0.9rem 2.4rem", fontWeight: 700, fontSize: "1.05rem", cursor: "pointer", boxShadow: "0 4px 20px rgba(198,37,45,0.3)", transition: "transform 0.2s, box-shadow 0.2s" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(-2px)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 8px 28px rgba(198,37,45,0.45)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.transform = "translateY(0)"; (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 4px 20px rgba(198,37,45,0.3)"; }}
                >
                  Reserve Your Community Table →
                </button>
              </a>
            </LightCard>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CommunityTable;
